import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Select,
	MenuItem,
	ListItemText,
	FormControl,
	InputLabel,
} from "@mui/material";
// cmp
import MuiFileInput from "./MuiFileInput";
// hooks
import useSend from "../../hooks/useSend";
// services
import { icons } from "@local/theme";
// types
import type { CmdUpdateTable, TableData, TableNameId } from "../../types/gupport";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

interface Metadata {
	id: IDWithKey | string;
	data: object;
}

interface IDWithKey {
	string_key: string;
}

type Props = Readonly<{
	tables: ReadonlyArray<TableData>;
}>;

const ImportTable = ({ tables }: Props) => {
	const send = useSend();
	const { t } = useTranslation();

	const [showDialog, setShowDialog] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [selectedTable, setSelectedTable] = useState<TableNameId | null>(null);
	const [file, setFile] = useState<File | null>(null);

	const handleClose = () => {
		setShowDialog(false);
		setIsUploading(false);
		setFile(null);
	};

	const handleTableChange = (event) => {
		setIsUploading(false);
		setSelectedTable(event.target.value);
		setFile(null);
	};

	const handleUploadClick = async () => {
		if (file) {
			if (file.size > MAX_FILE_SIZE) {
				const maxSizeMB = new Intl.NumberFormat(undefined, { style: "unit", unit: "megabyte" }).format(MAX_FILE_SIZE / 1024 / 1024);
				console.warn(`File exceeds the ${maxSizeMB} size limit`, file.name);
				return;
			}
			try {
				setIsUploading(true);

				const text = await file.text();
				const jsonData = JSON.parse(text) as ReadonlyArray<Metadata>;

				const cmd = {
					action: "updateTable",
					tableName: selectedTable!,
					data: jsonData.map((data) => ({
						id: (typeof data.id === "string") ? data.id : data.id.string_key,
						data: data.data,
					})),
				} as const satisfies CmdUpdateTable;
				send(cmd, (error, msg) => {
					if (!error && msg?.payload.status === "ok") {
						console.info("Imported successfully", msg.payload.data);
						setShowDialog(false);
					} else {
						console.warn("Upload failed", error, msg);
					}
					setIsUploading(false);
				});
			} catch (error) {
				setIsUploading(false);
				console.warn("Failed to read json data", error, file);
			}
		}
	};

	return (
		<>
			<Button
				variant="contained"
				startIcon={<icons.Download />}
				onClick={() => (setShowDialog(true))}
				sx={{ marginLeft: "8px" }}
			>
				{t("teditor.importTable.buttonTitle")}
			</Button>
			<Dialog
				fullWidth={true}
				open={showDialog}
				onClose={handleClose}
			>
				<DialogTitle>{t("teditor.importTable.uploadFile")}</DialogTitle>
				<DialogContent sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
					<FormControl sx={{ marginBottom: "16px" }}>
						<InputLabel shrink={true}>
							{t("teditor.importTable.table")}
						</InputLabel>
						<Select
							fullWidth={true}
							value={selectedTable}
							onChange={handleTableChange}
						>
							{tables.map((table) => (
								<MenuItem key={table.id} value={table.id}>
									<ListItemText primary={table.desc} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<MuiFileInput file={file} onChange={setFile} />
				</DialogContent>
				<DialogActions>
					<Button
						disabled={selectedTable === null || file === null}
						loading={isUploading}
						onClick={handleUploadClick}
						sx={{ marginLeft: "16px" }}
					>
						{t("teditor.importTable.uploadFile")}
					</Button>
					<Button color="inherit" onClick={handleClose}>{t("dialog.cancel")}</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ImportTable;
